export type ProjectStartingPoint =
  | "new_app"
  | "existing_app"
  | "operations"
  | "unsure";

export type ProjectRecommendation =
  | "APP_BUILD"
  | "APP_RESCUE"
  | "SVM_OPERATOR"
  | "SYSTEMS_AUDIT";

export interface ProjectTranslatorAnswers {
  challenge: string;
  startingPoint: ProjectStartingPoint;
  peopleAndWork: string;
  desiredChange: string;
  constraints: string;
}

export interface ProjectPlan {
  problemHeard: string;
  recommendation: ProjectRecommendation;
  recommendationReason: string;
  firstPhase: string[];
  unknowns: string[];
  nextConversation: string;
}

export interface ProjectPlanResult {
  plan: ProjectPlan;
  mode: "ai_assisted" | "guided";
  notice?: string;
}

export interface ProjectTranslatorConfig {
  enabled: boolean;
  endpoint: string;
}

export interface ProjectIntakeHandoff {
  configKey: "appBuild" | "appRescue" | "aiOperator" | "systemsAudit";
  initialValues: Record<string, string>;
}

const DEFAULT_PROJECT_TRANSLATOR_TIMEOUT_MS = 12_000;

const prohibitedPlanClaimPatterns = [
  /[$€£]\s*\d/i,
  /\b\d[\d,]*(?:\.\d+)?\s*(?:usd|dollars?|days?|weeks?|months?|years?)\b/i,
  /\b(?:guaranteed?|return on investment|roi|fixed price|final quote|final scope|delivery date|launch date)\b/i,
  /\b(?:use|choose|build (?:it|this) (?:with|on)|deploy (?:it|this) (?:with|on|to)|integrate (?:it|this) with)\s+(?:aws|azure|gcp|react|next\.?js|vue|angular|supabase|firebase|hubspot|salesforce|stripe)\b/i,
];

export const recommendationLabels: Record<ProjectRecommendation, string> = {
  APP_BUILD: "App Build",
  APP_RESCUE: "App Rescue",
  SVM_OPERATOR: "SVM Operator",
  SYSTEMS_AUDIT: "Systems Audit",
};

const recommendationConfigKeys: Record<ProjectRecommendation, ProjectIntakeHandoff["configKey"]> = {
  APP_BUILD: "appBuild",
  APP_RESCUE: "appRescue",
  SVM_OPERATOR: "aiOperator",
  SYSTEMS_AUDIT: "systemsAudit",
};

export function recommendProjectPath(answers: ProjectTranslatorAnswers): ProjectRecommendation {
  if (answers.startingPoint === "new_app") return "APP_BUILD";
  if (answers.startingPoint === "existing_app") return "APP_RESCUE";

  const context = `${answers.challenge} ${answers.peopleAndWork} ${answers.desiredChange}`.toLowerCase();
  const appRescueSignals = /\b(repo|prototype|preview|mvp|no-code|lovable|bubble|app is|application is|broken app|stalled app)\b/;
  const appBuildSignals = /\b(build an app|new app|mobile app|web app|portal|platform|customer app|staff app)\b/;
  const operatorSignals = /\b(intake|lead|request|approval|follow[- ]?up|handoff|assign|owner|status|queue|workflow|delivery|client update)\b/;

  if (appRescueSignals.test(context)) return "APP_RESCUE";
  if (appBuildSignals.test(context)) return "APP_BUILD";
  if (operatorSignals.test(context)) return "SVM_OPERATOR";

  return answers.startingPoint === "operations" ? "SVM_OPERATOR" : "SYSTEMS_AUDIT";
}

export function buildGuidedProjectPlan(answers: ProjectTranslatorAnswers): ProjectPlan {
  const recommendation = recommendProjectPath(answers);
  const shared = {
    problemHeard: `You described this problem: ${finishSentence(answers.challenge)}`,
  };

  if (recommendation === "APP_BUILD") {
    return {
      ...shared,
      recommendation,
      recommendationReason:
        "Start with an App Build. SVM can turn the job, users, and outcome you described into a focused first release before choosing the technical details.",
      firstPhase: [
        "Confirm who will use the app and the one job the first release must handle well.",
        "Map the information, permissions, decisions, and handoffs the app needs to support.",
        "Define a small first release that can be built, tested with real users, and supported after launch.",
      ],
      unknowns: buildUnknowns(answers, [
        "Which user should the first release serve first?",
        "What information or existing tools must the app work with?",
        "What result would prove the first release is useful?",
      ]),
      nextConversation:
        "Review the people, current process, and first useful outcome with SVM so we can shape the release before discussing features or technology.",
    };
  }

  if (recommendation === "APP_RESCUE") {
    return {
      ...shared,
      recommendation,
      recommendationReason:
        "Start with App Rescue. There is already something to inspect, so the fastest path is to find the real blockers before adding more features.",
      firstPhase: [
        "Review the app, repo, preview, or no-code build and reproduce the main failure.",
        "Separate launch blockers from improvements that can wait.",
        "Prepare a fix-and-verify plan for the smallest version people can use with confidence.",
      ],
      unknowns: buildUnknowns(answers, [
        "Where can SVM review the current app or build?",
        "Which part fails most often or blocks launch?",
        "Who needs to trust the app before it can move forward?",
      ]),
      nextConversation:
        "Walk through what exists, what happens when it fails, and what must be true for you to call the app ready.",
    };
  }

  if (recommendation === "SVM_OPERATOR") {
    return {
      ...shared,
      recommendation,
      recommendationReason:
        "Start with SVM Operator. The need appears to be keeping requests, decisions, ownership, follow-up, and delivery clear across ongoing work.",
      firstPhase: [
        "Choose one important workflow and trace it from the first request to the final outcome.",
        "Identify the owners, tools, decisions, and exceptions that make the work hard to run.",
        "Set up one clear flow that shows what came in, what needs a decision, and what happens next.",
      ],
      unknowns: buildUnknowns(answers, [
        "Which workflow causes the most missed work or uncertainty?",
        "Who owns each decision and follow-up today?",
        "Which tools must stay in place?",
      ]),
      nextConversation:
        "Map one real request from arrival through delivery so SVM can recommend the first Operator workflow without overbuilding it.",
    };
  }

  return {
    ...shared,
    recommendation,
    recommendationReason:
      "Start with a Systems Audit. The problem is clear enough to investigate, but the right build should follow a short review of how the work runs today.",
    firstPhase: [
      "Choose the painful workflow that costs the most time, trust, or missed opportunity.",
      "Map what people do today, where information lives, and where work gets repeated or lost.",
      "Recommend the first practical fix and whether it calls for process changes, automation, an app, or SVM Operator.",
    ],
    unknowns: buildUnknowns(answers, [
      "Where does the problem begin, and where is it first noticed?",
      "How often does it happen and who feels the impact?",
      "What has already been tried?",
    ]),
    nextConversation:
      "Walk through one recent example with SVM so we can find the first fix before recommending software.",
  };
}

export function getProjectTranslatorConfig(
  env: Record<string, string | boolean | undefined> = import.meta.env
): ProjectTranslatorConfig {
  const directEndpoint = typeof env.VITE_PROJECT_TRANSLATOR_ENDPOINT === "string"
    ? env.VITE_PROJECT_TRANSLATOR_ENDPOINT.trim()
    : "";
  const apiBase = typeof env.VITE_API_GATEWAY_URL === "string"
    ? env.VITE_API_GATEWAY_URL.trim().replace(/\/$/, "")
    : "";
  const endpoint = directEndpoint || (apiBase ? `${apiBase}/v1/public/chat` : "");
  const flag = env.VITE_PROJECT_TRANSLATOR_ENABLED;
  const explicitlyEnabled = flag === true || flag === "true";

  return {
    enabled: Boolean(endpoint) && explicitlyEnabled,
    endpoint,
  };
}

export async function draftProjectPlan(
  answers: ProjectTranslatorAnswers,
  config: ProjectTranslatorConfig = getProjectTranslatorConfig(),
  fetcher: typeof fetch = fetch,
  timeoutMs = DEFAULT_PROJECT_TRANSLATOR_TIMEOUT_MS
): Promise<ProjectPlanResult> {
  const guidedPlan = buildGuidedProjectPlan(answers);

  if (!config.enabled || !config.endpoint) {
    return {
      plan: guidedPlan,
      mode: "guided",
      notice: "A guided draft was prepared from your answers. SVM will review it before recommending a build.",
    };
  }

  const controller = new AbortController();
  const timeoutId = globalThis.setTimeout(
    () => controller.abort(),
    Math.max(1, timeoutMs)
  );

  try {
    const response = await fetcher(config.endpoint, {
      method: "POST",
      headers: { "content-type": "application/json" },
      signal: controller.signal,
      body: JSON.stringify({
        resourceType: "project_translator",
        action: "draft_plan",
        messages: [
          {
            role: "user",
            content: buildProjectTranslatorPrompt(answers, guidedPlan),
          },
        ],
      }),
    });

    if (!response.ok) {
      throw new Error(`Project translator returned ${response.status}.`);
    }

    const assistantText = extractAssistantText(await response.text());
    const plan = parseProjectPlan(assistantText, guidedPlan);

    return {
      plan,
      mode: "ai_assisted",
      notice: "This is an AI-assisted draft. A person at SVM reviews every plan before recommending work.",
    };
  } catch {
    return {
      plan: guidedPlan,
      mode: "guided",
      notice: "The AI planning service was not available, so we prepared a guided draft. SVM will still review it before recommending work.",
    };
  } finally {
    globalThis.clearTimeout(timeoutId);
  }
}

export function buildProjectIntakeHandoff(
  answers: ProjectTranslatorAnswers,
  result: ProjectPlanResult
): ProjectIntakeHandoff {
  const { plan } = result;
  const initialValues: Record<string, string> = {
    summary: plan.problemHeard,
    painPoints: answers.challenge,
    desiredOutcome: answers.desiredChange,
    projectPlanRecommendation: recommendationLabels[plan.recommendation],
    projectPlanReason: plan.recommendationReason,
    projectPlanFirstPhase: plan.firstPhase.join("\n"),
    projectPlanUnknowns: plan.unknowns.join("\n"),
    projectPlanNextConversation: plan.nextConversation,
    projectPlanMode: result.mode,
    translatorStartingPoint: answers.startingPoint,
    translatorPeopleAndWork: answers.peopleAndWork,
    translatorConstraints: answers.constraints,
  };

  if (plan.recommendation === "APP_BUILD") {
    initialValues.usersAndJob = answers.peopleAndWork;
    initialValues.currentProcess = answers.challenge;
    initialValues.mustWorkFirst = answers.desiredChange;
  } else if (plan.recommendation === "APP_RESCUE") {
    initialValues.brokenOrUnfinished = answers.challenge;
  } else if (plan.recommendation === "SVM_OPERATOR") {
    initialValues.operatorScope = answers.desiredChange;
  } else {
    initialValues.bottlenecks = answers.challenge;
  }

  return {
    configKey: recommendationConfigKeys[plan.recommendation],
    initialValues,
  };
}

export function extractAssistantText(raw: string): string {
  const chunks: string[] = [];

  for (const line of raw.split(/\r?\n/)) {
    if (!line.startsWith("data:")) continue;
    const value = line.slice(5).trim();
    if (!value || value === "[DONE]") continue;

    try {
      const parsed = JSON.parse(value) as {
        choices?: Array<{ delta?: { content?: unknown }; message?: { content?: unknown } }>;
      };
      const content = parsed.choices?.[0]?.delta?.content ?? parsed.choices?.[0]?.message?.content;
      if (typeof content === "string") chunks.push(content);
    } catch {
      // Ignore non-SSE lines and let the plain-text fallback below handle them.
    }
  }

  return chunks.length > 0 ? chunks.join("") : raw;
}

export function parseProjectPlan(content: string, fallback: ProjectPlan): ProjectPlan {
  const firstBrace = content.indexOf("{");
  const lastBrace = content.lastIndexOf("}");
  if (firstBrace === -1 || lastBrace <= firstBrace) {
    throw new Error("Project translator did not return a structured plan.");
  }

  const parsed = JSON.parse(content.slice(firstBrace, lastBrace + 1)) as Record<string, unknown>;
  const recommendation = normalizeRecommendation(
    parsed.recommendation ?? parsed.recommended_path,
    fallback.recommendation
  );

  const plan: ProjectPlan = {
    problemHeard: safeString(parsed.problemHeard ?? parsed.problem_heard, fallback.problemHeard),
    recommendation,
    recommendationReason: safeString(
      parsed.recommendationReason ?? parsed.recommendation_reason,
      fallback.recommendationReason
    ),
    firstPhase: safeStringArray(parsed.firstPhase ?? parsed.first_phase, fallback.firstPhase),
    unknowns: safeStringArray(parsed.unknowns, fallback.unknowns),
    nextConversation: safeString(
      parsed.nextConversation ?? parsed.next_conversation,
      fallback.nextConversation
    ),
  };

  if (containsProhibitedPlanClaim(plan)) {
    throw new Error("Project translator returned a prohibited commitment.");
  }

  return plan;
}

function containsProhibitedPlanClaim(plan: ProjectPlan): boolean {
  const recommendationText = [
    plan.recommendationReason,
    ...plan.firstPhase,
    ...plan.unknowns,
    plan.nextConversation,
  ].join(" ");

  return prohibitedPlanClaimPatterns.some((pattern) => pattern.test(recommendationText));
}

function buildProjectTranslatorPrompt(answers: ProjectTranslatorAnswers, guidedPlan: ProjectPlan): string {
  return `You are SVM's AI Capacity Snapshot. Turn a nontechnical business owner's description into a calm, practical first plan.

Rules:
- Use plain language, short sentences, and concrete nouns.
- Do not promise price, timeline, ROI, architecture, integrations, or a final scope.
- Do not imply that AI made a final decision. A person at SVM reviews every plan.
- Recommend exactly one path: APP_BUILD, APP_RESCUE, SVM_OPERATOR, or SYSTEMS_AUDIT.
- Treat the guided recommendation as a useful starting point, but change it if the answers clearly support another path.
- Return JSON only. Do not use markdown.

Required JSON shape:
{
  "problem_heard": "string",
  "recommendation": "APP_BUILD | APP_RESCUE | SVM_OPERATOR | SYSTEMS_AUDIT",
  "recommendation_reason": "string",
  "first_phase": ["three short steps"],
  "unknowns": ["up to three useful questions"],
  "next_conversation": "string"
}

Visitor answers:
- What is frustrating, slow, or stuck: ${answers.challenge}
- Starting point: ${answers.startingPoint}
- People and work involved: ${answers.peopleAndWork}
- What should change: ${answers.desiredChange}
- Timing, tools, or limits: ${answers.constraints || "Not provided"}

Guided starting recommendation: ${guidedPlan.recommendation}`;
}

function buildUnknowns(
  answers: ProjectTranslatorAnswers,
  defaults: [string, string, string]
): string[] {
  if (!answers.constraints.trim()) return defaults;
  return [defaults[0], defaults[1], `Which parts of these limits are fixed: ${trimForQuestion(answers.constraints)}`];
}

function normalizeRecommendation(value: unknown, fallback: ProjectRecommendation): ProjectRecommendation {
  if (typeof value !== "string") return fallback;
  const normalized = value.trim().toUpperCase().replace(/[ -]+/g, "_");
  if (
    normalized === "APP_BUILD" ||
    normalized === "APP_RESCUE" ||
    normalized === "SVM_OPERATOR" ||
    normalized === "SYSTEMS_AUDIT"
  ) {
    return normalized;
  }
  return fallback;
}

function safeString(value: unknown, fallback: string): string {
  if (typeof value !== "string" || !value.trim()) return fallback;
  return value.trim().slice(0, 700);
}

function safeStringArray(value: unknown, fallback: string[]): string[] {
  if (!Array.isArray(value)) return fallback;
  const strings = value
    .filter((item): item is string => typeof item === "string" && Boolean(item.trim()))
    .map((item) => item.trim().slice(0, 320))
    .slice(0, 4);
  return strings.length > 0 ? strings : fallback;
}

function finishSentence(value: string): string {
  const text = value.trim();
  if (!text) return "The current way of working is not giving you a reliable next step.";
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

function trimForQuestion(value: string): string {
  const text = value.trim().replace(/[.!?]+$/, "");
  return text.length > 140 ? `${text.slice(0, 137)}...` : text;
}
